import { useComplianceAreaQuery } from "../../common/hooks/useSelectableItemQueries.ts";
import { FrameworkTemplateSaveModel } from "./CreateEditTemplateDialog.types.ts";
import { useState } from "react";
import { post, put } from "../../common/api/apiShared.ts";
import { useNavigate } from "react-router";
import { useUrlProvider } from "../../useUrlProvider.ts";
import { CreateEditTemplateDialogProps } from "./CreateEditTemplateDialog.tsx";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useCreateEditTemplateDialog(props: CreateEditTemplateDialogProps) {
    const navigate = useNavigate();
    const urlProvider = useUrlProvider();
    const snackbar = useDotLegalSnackbar();
    const isEdit = props.editModel !== undefined;

    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [saveModel, setSaveModel] = useState(initSaveModel());
    const [showErrors, setShowErrors] = useState(false);

    const complianceAreaQuery = useComplianceAreaQuery();

    const modelUpdater = {
        onNameChange: (name: string | undefined, lang: string) => {
            saveModel.languages.find((l) => l.twoLetterIsoLanguage === lang)!.name = name ?? "";
            const updatedModel = saveModel.withUpdates({ languages: saveModel.languages });
            setSaveModel(updatedModel);
        },
        onDescriptionChange: (description: string | undefined, lang: string) => {
            saveModel.languages.find((l) => l.twoLetterIsoLanguage === lang)!.description = description;
            const updatedModel = saveModel.withUpdates({ languages: saveModel.languages });
            setSaveModel(updatedModel);
        },
        onComplianceAreaChange: (complianceAreas: Array<string>) => {
            const updatedModel = saveModel.withUpdates({ complianceAreas });
            setSaveModel(updatedModel);
        },
        onTemplatePlanRequirementsChange: (planRequirements: Array<string>) => {
            const updatedModel = saveModel.withUpdates({ templatePlanRequirements: planRequirements.map((x) => Number(x)) });
            setSaveModel(updatedModel);
        },
    };

    const createFrameworkTemplateMutation = useDotLegalMutation(createFrameworkTemplateApi);
    const createTemplate = async () => {
        await createFrameworkTemplateMutation.mutateAsync(undefined, {
            onSuccess: (response: any) => {
                navigate(urlProvider.getFrameworkTemplateOverviewUrl(response.value()));
            },
        });
    };

    const editFrameworkTemplateMutation = useDotLegalMutation(editFrameworkTemplateApi);
    const editTemplate = async () => {
        await editFrameworkTemplateMutation.mutateAsync(undefined, {
            onSuccess: (response: any) => {
                snackbar.show("Template saved", "success");
                if (props.onTemplateEdited) {
                    props.onTemplateEdited();
                }
            },
        });
    };

    const onSaveTemplate = async () => {
        if (saveModel.isInvalid()) {
            setShowErrors(true);
            return;
        }

        if (isEdit) {
            await editTemplate();
        } else {
            await createTemplate();
        }
    };

    return {
        isEdit,
        saveModel,
        modelUpdater,
        showErrors,
        isSaving: createFrameworkTemplateMutation.isLoading,
        complianceAreaIsLoading: complianceAreaQuery.isLoading,
        complianceAreas: complianceAreaQuery.data ?? [],
        onSaveTemplate,
        selectedTabIndex,
        setSelectedTabIndex,
    };

    function createFrameworkTemplateApi() {
        return post("/frameworks/templates", saveModel);
    }

    function editFrameworkTemplateApi() {
        return put(`/frameworks/templates/${props.editModel!.templateId}`, saveModel);
    }

    function initSaveModel() {
        return isEdit ? props.editModel!.model! : FrameworkTemplateSaveModel.createEmptyModel();
    }
}
